/**
 * Способ разгрузки газобетона — единственное место, где он читается и меняется.
 *
 * Значение живёт в общем состоянии `deliveryMethod.gazUnloadMethod` (см. App.js).
 * Второго состояния для него не заводится: и переключатель в разделе «Товары», и
 * прежний селектор в «Способах доставки» ходят через эти функции, поэтому после
 * переноса поля расчёты и восстановление состояния читают то же самое значение.
 *
 * Своей логики выбора транспорта, вместимости и стоимости здесь нет — это только
 * чтение и запись одного поля общего состояния.
 */

// Расширение в пути обязательно: модуль загружается не только сборщиком, но и
// node-тестами напрямую (см. tests/gazobeton/gaz_unload_switch.test.js).
import { UNLOAD_METHOD_OPTIONS } from './deliveryMethodsHelpers.js';

/** Значение по умолчанию — то же, что подставлял прежний селектор. */
export const GAZ_UNLOAD_DEFAULT_METHOD = 'без разгрузки';

/**
 * Способы разгрузки поимённо. Значения — те же строки, что стоят в
 * UNLOAD_METHOD_OPTIONS и приходят обратно в расчёте
 * (`deliveryBreakdown.unloadMethod`). Второго списка способов и второго
 * состояния для них не заводится; совпадение со списком сверяет тест.
 */
export const GAZ_UNLOAD_MANIPULATOR_METHOD = 'разгрузка манипулятором';
export const GAZ_UNLOAD_CRANE_METHOD = 'разгрузка краном';

export { UNLOAD_METHOD_OPTIONS };

// Способ приходит из расчёта строкой; сравнивается он приведённым, а не как
// попало записанным. Своего разбора значения здесь нет — только нормализация.
const normalizeUnloadMethodValue = (method) => String(method ?? '').trim().toLowerCase();

/**
 * Выбрана ли разгрузка манипулятором. Пустой и неизвестный способ — нет:
 * пока способ не определён, разгрузку никому не приписываем.
 */
export const isGazManipulatorUnloadMethod = (method) => (
  normalizeUnloadMethodValue(method) === GAZ_UNLOAD_MANIPULATOR_METHOD
);

/** Заказана ли разгрузка вообще — манипулятором или краном. */
export const isGazUnloadingOrderedMethod = (method) => {
  const value = normalizeUnloadMethodValue(method);
  return value === GAZ_UNLOAD_MANIPULATOR_METHOD || value === GAZ_UNLOAD_CRANE_METHOD;
};

/** Текущий способ разгрузки заказа. */
export const getGazUnloadMethod = (deliveryMethod) => {
  const value = deliveryMethod?.gazUnloadMethod;
  return typeof value === 'string' && value ? value : '';
};

/**
 * Записывает выбранный способ в общее состояние.
 * Возвращает false, если менять нечего — вызывающий код может не трогать свои
 * вспомогательные состояния.
 */
export const applyGazUnloadMethod = (setDeliveryMethod, method) => {
  if (typeof setDeliveryMethod !== 'function') {
    return false;
  }
  if (!UNLOAD_METHOD_OPTIONS.includes(method)) {
    return false;
  }

  setDeliveryMethod((prev) => {
    if (prev?.gazUnloadMethod === method) {
      return prev;
    }
    return { ...prev, gazUnloadMethod: method };
  });

  return true;
};

export default applyGazUnloadMethod;
